import type { ServiceCategory } from 'prisma/client'
import type { Schema } from 'yup'
import { object, string } from 'yup'

type SchemaKeys = 'name' | 'color' | 'icon'

export function serviceCategorySchema(fields: SchemaKeys[], t: Function) {
  const baseSchema = {
    name: string().required(t('errors.required')).min(3, t('errors.min', { min: 3 })),
    color: string().required(t('errors.required')),
    icon: string().required(t('errors.required')),
  }
  const partialSchema: Partial<Record<SchemaKeys, Schema<any>>> = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const useServiceCategoryStore = defineStore('service-category', () => {
  const categories = ref<ServiceCategory[]>([])
  const categoriesCache = new Map<number, ServiceCategory[]>()
  const normalizeData = (service: any) => {
    return service
  }
  async function fetch(activityId: number) {
    if (categoriesCache.has(activityId)) {
      categories.value = categoriesCache.get(activityId) || []
      return { data: categories.value, status: 'success', error: null }
    }
    try {
      const result = await $fetch<ServiceCategory[] | null>('/api/service/category', {
        query: { activityId },
      })
      if (result) {
        categoriesCache.set(activityId, result)
        categories.value = result
      }
      return result
    } catch (e) {
      return { data: categories.value, status: 'error', error: e }
    }
  }
  async function upsert(category: Partial<ServiceCategory>) {
    try {
      const result = await $fetch<ServiceCategory | null>('/api/service/category', {
        method: 'POST',
        body: normalizeData(category),
      })
      if (result) {
        const index = categories.value?.findIndex(s => s.id === result.id)
        if (index !== undefined && index > -1) {
          categories.value?.splice(index, 1, result)
          categoriesCache.set(result.activityId, categories.value)
        } else {
          categories.value = [...(categories.value || []), result]
          categoriesCache.set(result.activityId, categories.value)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data }
    }
  }
  async function remove(id: number) {
    try {
      const result = await $fetch<ServiceCategory | null>(`/api/service/category`, {
        method: 'DELETE',
        query: { id },
      })
      if (result) {
        const index = categories.value?.findIndex(s => s.id === id)
        if (index !== undefined && index > -1) {
          const activityId = categories.value[index].activityId
          categories.value?.splice(index, 1)
          categoriesCache.set(activityId, categories.value)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data }
    }
  }
  return { fetch, upsert, remove, normalizeData, categories }
})